import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { ConfirmationChip, ErrorNotice, FreshnessLabel, Provenance, type Confirmation, type Freshness } from '@bounded/ui/state';
import { Button, Facts, Field, Input, Page, Panel, SettingRow, Skeleton, rowClass } from '@bounded/ui/primitives';
import { useIdempotencyKey } from '@bounded/ui/idempotency';
import { useFormat, useT } from '../../i18n';
import { DaemonVersion, UpdateDevice } from '../../components/daemon-version';

export const Route = createFileRoute('/_app/devices/$deviceId')({ component: DevicePage });

const modelState: Record<string, Confirmation> = {
  configured: 'confirmed', credential_unavailable: 'rejected', configuration_mismatch: 'pending', not_configured: 'unknown', disabled_in_catalog: 'unknown',
};

function DevicePage() {
  const t = useT(); const m = t.device; const f = useFormat();
  const { deviceId } = Route.useParams();
  const devices = useQuery(q.devices());
  const runtime = useQuery(q.runtime(deviceId));
  const models = useQuery(q.deviceModels(deviceId));
  const device = devices.data?.items.find((d) => d.id === deviceId);
  const r = runtime.data;

  return (
    <Page>
      <div>
        <p className="text-sm text-graphite"><Link to="/devices" className="hover:underline">{t.devices.title}</Link></p>
        <h1 className="mt-1 text-2xl font-semibold tracking-[-0.01em]">{device?.name ?? deviceId}</h1>
        {r && <p className="mt-1"><FreshnessLabel freshness={r.connection as Freshness} at={r.received_at} /></p>}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title={m.service}>
          <div className="p-4">
            {!r ? <Skeleton className="h-20" /> : r.snapshot ? (
              <Facts items={[
                [m.version, <DaemonVersion version={r.snapshot.daemon_version} />],
                [m.system, r.snapshot.platform === 'darwin' ? 'macOS' : 'Linux'],
                [m.auto, r.snapshot.disabled ? <span className="text-deny">{m.autoOff}</span> : m.autoOn],
                [m.pendingReports, r.snapshot.pending_reports],
                [m.paired, device ? f.dateTime(device.created_at) : '—'],
              ]} />
            ) : <p className="text-graphite">{m.notReported(<code>bounded</code>)}</p>}
            {r?.snapshot && <div className="mt-4"><UpdateDevice version={r.snapshot.daemon_version} /></div>}
          </div>
        </Panel>
        <Panel title={m.agents}>
          {r?.snapshot?.adapters.length ? (
            <ul>{r.snapshot.adapters.map((a) => (
              <li key={a.id} className={`flex items-center justify-between gap-3 ${rowClass}`}>
                <span className="font-medium">{t.agents[a.profile] ?? a.id}</span>
                <span className="text-graphite">{m.sessions(a.active_sessions)}</span>
              </li>
            ))}</ul>
          ) : <p className="p-4 text-graphite">{m.noAgents(<code>bounded hook-install</code>)}</p>}
        </Panel>
      </div>

      <Folders deviceId={deviceId} />

      <Panel title={m.models} description={m.modelsDescription} footer={models.data && models.data.freshness !== 'recent' && <Provenance>{m.modelsStale}</Provenance>}>
        {models.data?.items.map((x) => (
          <div key={x.id} className={`flex flex-wrap items-center justify-between gap-2 ${rowClass}`}>
            <span><span className="font-medium">{x.name}</span> <code className="text-sm text-graphite">{x.id}</code></span>
            <ConfirmationChip state={modelState[x.state] ?? 'unknown'}>{m.modelState[x.state] ?? x.state}</ConfirmationChip>
          </div>
        ))}
      </Panel>

      {device && !device.revoked_at && <Revoke deviceId={deviceId} name={device.name} revision={device.revision} />}
      {device?.revoked_at && <p className="text-graphite">{m.revoked(f.dateTime(device.revoked_at))}</p>}
    </Page>
  );
}

function Revoke({ deviceId, name, revision }: { deviceId: string; name: string; revision: number }) {
  const t = useT(); const m = t.device;
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const queryClient = useQueryClient();
  const keyFor = useIdempotencyKey();
  const revoke = useMutation({
    mutationFn: () => { const body = { expected_revision: revision, reason: 'user_revoked_from_console' }; return api.revokeDevice(deviceId, body, keyFor(body)); },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['devices'] }),
  });
  return (
    <Panel className="border-deny/30">
      {/* Danger zone as a setting row: consequence on the left, action on the right. */}
      <SettingRow label={m.revokeTitle} description={<>{m.revokeDescription} {m.revokeBody}</>}
        control={!open && <Button className="w-full" variant="danger" onClick={() => setOpen(true)}>{m.revokeStart(name)}</Button>} />
      {(open || revoke.error) && (
        <div className="grid gap-3 border-t border-rule px-4 py-4">
          {revoke.error && <ErrorNotice error={revoke.error} onRefresh={() => void queryClient.invalidateQueries({ queryKey: ['devices'] })} />}
          {open && (
            <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); revoke.mutate(); }}>
              <div className="w-full max-w-xs"><Field label={m.revokeConfirm(name)} htmlFor="confirm-name"><Input id="confirm-name" autoComplete="off" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} /></Field></div>
              <Button type="submit" variant="danger" disabled={typed !== name || revoke.isPending}>{m.revokeSubmit}</Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>{t.common.cancel}</Button>
            </form>
          )}
        </div>
      )}
    </Panel>
  );
}

/** Where agents on this device have worked, and which project governs each folder. */
function Folders({ deviceId }: { deviceId: string }) {
  const t = useT(); const m = t.workspace;
  const folders = useQuery(q.workspaces());
  const items = (folders.data?.items ?? []).filter((w) => w.device_id === deviceId);
  return (
    <Panel title={m.devicePanel} description={m.devicePanelDescription}>
      {folders.isPending && <div className="p-4"><Skeleton className="h-12" /></div>}
      {folders.error && <div className="p-4"><ErrorNotice error={folders.error} onRetry={() => void folders.refetch()} /></div>}
      {folders.data && !items.length && <p className="p-4 text-graphite">{m.none}</p>}
      <ul>{items.map((w) => (
        <li key={w.id} className={`flex flex-wrap items-center justify-between gap-2 ${rowClass}`}>
          <code className="min-w-0 [overflow-wrap:anywhere]">{w.path}</code>
          <Link to="/workspaces/$workspaceId" params={{ workspaceId: w.id }} className="text-sm hover:underline">
            {w.state === 'linked' ? <span>{w.project_id}</span> : w.state === 'pending' ? <span className="text-ask">{m.waiting}</span> : <span className="text-graphite">{m.notProtected}</span>}
          </Link>
        </li>
      ))}</ul>
    </Panel>
  );
}
